import { z } from 'zod';
import { PageQuerySchema } from './common';

/**
 * Stages & projets de fin d'études (Phase 10). A school defines its natures of internship (stage d'été, PFE…);
 * an internship is declared by a student or created by the registrar, its subject approved, an academic
 * supervisor accepts it, the student deposits the documents and defends before a jury.
 */

export const INTERNSHIP_STATUSES = ['proposed', 'to_revise', 'approved', 'refused'] as const;
export type InternshipStatus = (typeof INTERNSHIP_STATUSES)[number];

export const SUPERVISION_STATUSES = ['none', 'pending', 'accepted', 'refused'] as const;
export type SupervisionStatus = (typeof SUPERVISION_STATUSES)[number];

/** Files a student deposits (the nature says which ones are required before the defence). */
export const INTERNSHIP_DOCUMENT_KINDS = [
  'report',
  'certificate',
  'plagiarism',
  'logbook',
  'company_evaluation',
] as const;
export type InternshipDocumentKind = (typeof INTERNSHIP_DOCUMENT_KINDS)[number];

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null));
const phone = z
  .string()
  .trim()
  .regex(/^\+?[0-9 ]{8,20}$/, 'internships.errors.phone')
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));
const email = z
  .email('internships.errors.email')
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

// ── Natures ─────────────────────────────────────────────────────────────────
export const InternshipTypeInputSchema = z.object({
  name: z.string().trim().min(2, 'internships.errors.name').max(80),
  isPfe: z.boolean().default(false),
  /** Levels whose students do this internship (empty = every level). */
  levelIds: z.array(z.uuid()).max(50).default([]),
  /** Expected length, informative (convention, lists). */
  durationWeeks: z.number().int().min(1).max(52).nullable().default(null),
  /** 1 = alone, 2 = binôme, 3 = trinôme. */
  maxStudents: z.number().int().min(1).max(3).default(1),
  /** Students declare their own internship from their space. */
  studentCanDeclare: z.boolean().default(true),
  /** A host-company supervisor (name, e-mail) is required. */
  requiresCompanySupervisor: z.boolean().default(true),
  /** Ends with a defence before a jury. */
  hasDefense: z.boolean().default(false),
  defenseMinutes: z.number().int().min(10).max(180).default(30),
  /** Documents that must be deposited before the defence can be scheduled. */
  requiredDocuments: z.array(z.enum(INTERNSHIP_DOCUMENT_KINDS)).default(['report']),
  active: z.boolean().default(true),
});
export type InternshipTypeInput = z.input<typeof InternshipTypeInputSchema>;

export const InternshipTypeSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  isPfe: z.boolean(),
  levelIds: z.array(z.uuid()),
  durationWeeks: z.number().int().nullable(),
  maxStudents: z.number().int(),
  studentCanDeclare: z.boolean(),
  requiresCompanySupervisor: z.boolean(),
  hasDefense: z.boolean(),
  defenseMinutes: z.number().int(),
  requiredDocuments: z.array(z.enum(INTERNSHIP_DOCUMENT_KINDS)),
  active: z.boolean(),
  /** Internships of this nature (all years): a used nature cannot be deleted, only deactivated. */
  used: z.number().int(),
});
export type InternshipType = z.infer<typeof InternshipTypeSchema>;

// ── Internships ─────────────────────────────────────────────────────────────
const InternshipFieldsObject = z.object({
  typeId: z.uuid('internships.errors.type'),
  title: z.string().trim().min(5, 'internships.errors.title').max(200),
  description: text(2000),
  companyName: z.string().trim().min(2, 'internships.errors.company').max(160),
  companyCity: text(80),
  companyAddress: text(200),
  companySupervisorName: text(120),
  companySupervisorEmail: email,
  companySupervisorPhone: phone,
  startsOn: z.iso.date('internships.errors.date'),
  endsOn: z.iso.date('internships.errors.date'),
});
const datesInOrder = (v: { startsOn: string; endsOn: string }) => v.endsOn >= v.startsOn;

/** Declared by a student (their own enrollment) or created by the registrar for one to three students. */
export const InternshipInputSchema = InternshipFieldsObject.extend({
  /** Staff only: the students (the student's own declaration ignores it). */
  studentIds: z.array(z.uuid()).min(1).max(3).optional(),
  /** Student declaration: a teacher asked to supervise (staff assign directly). */
  supervisorId: z.uuid().nullable().optional(),
}).refine(datesInOrder, { path: ['endsOn'], message: 'internships.errors.range' });
export type InternshipInput = z.input<typeof InternshipInputSchema>;

export const InternshipUpdateSchema = InternshipFieldsObject.refine(datesInOrder, {
  path: ['endsOn'],
  message: 'internships.errors.range',
});
export type InternshipUpdate = z.input<typeof InternshipUpdateSchema>;

export const InternshipDecisionSchema = z
  .object({
    status: z.enum(['approved', 'to_revise', 'refused']),
    reason: z.string().trim().max(500).optional(),
  })
  .refine((d) => d.status === 'approved' || (d.reason?.length ?? 0) >= 3, {
    path: ['reason'],
    message: 'internships.errors.reason',
  });
export type InternshipDecision = z.input<typeof InternshipDecisionSchema>;

/** Staff assign a supervisor (accepted at once); a student proposes one (the teacher answers). */
export const SupervisorInputSchema = z.object({ teacherId: z.uuid() });
export const SupervisionAnswerSchema = z.object({ accept: z.boolean() });

const PersonRef = z.object({ id: z.uuid(), name: z.string() });

export const InternshipRowSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  title: z.string(),
  typeId: z.uuid(),
  typeName: z.string(),
  isPfe: z.boolean(),
  status: z.enum(INTERNSHIP_STATUSES),
  companyName: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  students: z.array(
    z.object({ id: z.uuid(), name: z.string(), matricule: z.string(), group: z.string().nullable() }),
  ),
  supervisor: PersonRef.nullable(),
  supervisionStatus: z.enum(SUPERVISION_STATUSES),
  /** Required documents deposited / required. */
  documents: z.object({ deposited: z.number().int(), required: z.number().int() }),
  defense: z.object({ startsAt: z.string(), room: z.string().nullable(), published: z.boolean() }).nullable(),
  grade: z.number().nullable(),
});
export type InternshipRow = z.infer<typeof InternshipRowSchema>;

export const InternshipDocumentSchema = z.object({
  kind: z.enum(INTERNSHIP_DOCUMENT_KINDS),
  fileName: z.string(),
  uploadedAt: z.string(),
  uploadedByName: z.string().nullable(),
});
export type InternshipDocument = z.infer<typeof InternshipDocumentSchema>;

export const InternshipDetailSchema = InternshipRowSchema.extend({
  academicYearId: z.uuid(),
  description: z.string().nullable(),
  companyCity: z.string().nullable(),
  companyAddress: z.string().nullable(),
  companySupervisorName: z.string().nullable(),
  companySupervisorEmail: z.string().nullable(),
  companySupervisorPhone: z.string().nullable(),
  statusReason: z.string().nullable(),
  type: InternshipTypeSchema,
  files: z.array(InternshipDocumentSchema),
  defenseDetail: z
    .object({
      id: z.uuid(),
      sessionId: z.uuid(),
      sessionTitle: z.string(),
      startsAt: z.string(),
      durationMinutes: z.number().int(),
      room: z.string().nullable(),
      president: PersonRef.nullable(),
      reviewer: PersonRef.nullable(),
      guest: z.string().nullable(),
      published: z.boolean(),
    })
    .nullable(),
  /** Convention de stage already issued (document id), if any. */
  conventionId: z.uuid().nullable(),
  can: z.object({
    edit: z.boolean(),
    decide: z.boolean(),
    assignSupervisor: z.boolean(),
    proposeSupervisor: z.boolean(),
    answerSupervision: z.boolean(),
    deposit: z.boolean(),
    issueConvention: z.boolean(),
    grade: z.boolean(),
  }),
});
export type InternshipDetail = z.infer<typeof InternshipDetailSchema>;

export const InternshipListQuerySchema = PageQuerySchema.extend({
  yearId: z.uuid(),
  typeId: z.uuid().optional(),
  status: z.enum(INTERNSHIP_STATUSES).optional(),
  /** Teacher views: supervised by me / where I sit on the jury / supervision requests to answer. */
  role: z.enum(['supervisor', 'jury', 'requests']).optional(),
});
export type InternshipListQuery = z.input<typeof InternshipListQuerySchema>;

// ── Defences ────────────────────────────────────────────────────────────────
export const DefenseSessionInputSchema = z
  .object({
    academicYearId: z.uuid(),
    title: z.string().trim().min(3, 'internships.errors.title').max(120),
    typeId: z.uuid().nullable().default(null),
    startsOn: z.iso.date('internships.errors.date'),
    endsOn: z.iso.date('internships.errors.date'),
    /** Students deposit their documents in this window (null = any time before the defence). */
    depositFrom: z.iso.date().nullable().default(null),
    depositUntil: z.iso.date().nullable().default(null),
  })
  .refine((s) => s.endsOn >= s.startsOn, { path: ['endsOn'], message: 'internships.errors.range' })
  .refine((s) => !s.depositFrom || !s.depositUntil || s.depositUntil >= s.depositFrom, {
    path: ['depositUntil'],
    message: 'internships.errors.range',
  });
export type DefenseSessionInput = z.input<typeof DefenseSessionInputSchema>;

export const DefenseSessionSchema = z.object({
  id: z.uuid(),
  academicYearId: z.uuid(),
  title: z.string(),
  typeId: z.uuid().nullable(),
  typeName: z.string().nullable(),
  startsOn: z.string(),
  endsOn: z.string(),
  depositFrom: z.string().nullable(),
  depositUntil: z.string().nullable(),
  scheduled: z.number().int(),
  published: z.number().int(),
});
export type DefenseSession = z.infer<typeof DefenseSessionSchema>;

export const DefenseInputSchema = z.object({
  sessionId: z.uuid(),
  internshipId: z.uuid(),
  /** ISO date-time (local school time, e.g. 2027-06-28T09:00). */
  startsAt: z.iso.datetime({ local: true, message: 'internships.errors.date' }),
  roomId: z.uuid().nullable().default(null),
  presidentId: z.uuid('internships.errors.president'),
  reviewerId: z.uuid('internships.errors.reviewer'),
  guest: z.string().trim().max(120).nullable().default(null),
  /** Schedule despite the clashes reported by the check (same answer as the timetable). */
  force: z.boolean().default(false),
});
export type DefenseInput = z.input<typeof DefenseInputSchema>;

export const DEFENSE_CLASHES = ['room', 'president', 'reviewer', 'supervisor', 'student'] as const;
export const DefenseClashSchema = z.object({
  kind: z.enum(DEFENSE_CLASHES),
  /** The other defence at the same time. */
  internshipTitle: z.string(),
  startsAt: z.string(),
});
export type DefenseClash = z.infer<typeof DefenseClashSchema>;

export const DefenseRowSchema = z.object({
  id: z.uuid(),
  internshipId: z.uuid(),
  internshipCode: z.string(),
  internshipTitle: z.string(),
  students: z.array(z.string()),
  supervisor: z.string().nullable(),
  startsAt: z.string(),
  durationMinutes: z.number().int(),
  roomId: z.uuid().nullable(),
  room: z.string().nullable(),
  presidentId: z.uuid(),
  president: z.string(),
  reviewerId: z.uuid(),
  reviewer: z.string(),
  guest: z.string().nullable(),
  published: z.boolean(),
  grade: z.number().nullable(),
});
export type DefenseRow = z.infer<typeof DefenseRowSchema>;

export const DefenseSessionDetailSchema = DefenseSessionSchema.extend({
  defenses: z.array(DefenseRowSchema),
  /** Approved internships of the session's nature, ready (required documents deposited) or not. */
  candidates: z.array(
    z.object({
      internshipId: z.uuid(),
      code: z.string(),
      title: z.string(),
      students: z.array(z.string()),
      supervisorId: z.uuid().nullable(),
      supervisor: z.string().nullable(),
      ready: z.boolean(),
      missing: z.array(z.enum(INTERNSHIP_DOCUMENT_KINDS)),
    }),
  ),
});
export type DefenseSessionDetail = z.infer<typeof DefenseSessionDetailSchema>;

export const DefenseGradeSchema = z.object({ grade: z.number().min(0).max(20).nullable() });

/** Teachers a student may ask to supervise, and the jury lists. */
export const TeacherOptionSchema = z.object({ id: z.uuid(), name: z.string() });
export type TeacherOption = z.infer<typeof TeacherOptionSchema>;

/** File limit for deposits (PDF reports with annexes; the proxy accepts 12 MB). */
export const INTERNSHIP_FILE_MAX_BYTES = 10 * 1024 * 1024;
